import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { KnowledgeArticle, allKnowledgeCategoryLabel, fetchPublicKnowledgeArticles } from '@/services/knowledge';
import { withImageFallback } from '@/utils/images';

export default function Knowledge() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(allKnowledgeCategoryLabel);
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [featuredArticle, setFeaturedArticle] = useState<KnowledgeArticle | null>(null);
  const [knowledgeCategories, setKnowledgeCategories] = useState<string[]>([allKnowledgeCategoryLabel]);
  const [loadingArticles, setLoadingArticles] = useState(true);

  // 讀取 URL 搜索參數
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  useEffect(() => {
    let alive = true;
    setLoadingArticles(true);
    fetchPublicKnowledgeArticles()
      .then((data) => {
        if (!alive) return;
        setArticles(data.articles);
        setFeaturedArticle(data.featuredArticle);
        setKnowledgeCategories(data.categories);
      })
      .finally(() => {
        if (alive) setLoadingArticles(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const filtered = articles.filter((a) => {
    const matchCategory = activeCategory === allKnowledgeCategoryLabel || a.category === activeCategory;
    const matchSearch =
      a.title.includes(searchQuery) ||
      a.summary.includes(searchQuery) ||
      a.category.includes(searchQuery) ||
      a.content.replace(/<[^>]*>/g, '').includes(searchQuery);
    return matchCategory && matchSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* 頁面標題 */}
        <section className="relative text-white pt-8 md:pt-14 pb-8 md:pb-14">
          {/* Decorative Layer (clipped) */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Solid Dark Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F2A1C] via-[#1B3D2A] to-[#0F2A1C]" />
            {/* Background Image - subtle texture only */}
            <div className="absolute inset-0 opacity-[0.06]">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20elegant%20knowledge%20and%20learning%20concept%20with%20soft%20layered%20geometric%20shapes%20overlapping%20circles%20and%20flowing%20lines%20in%20warm%20cream%20and%20sage%20green%20tones%20minimalist%20sophisticated%20editorial%20style%20subtle%20texture%20depth%20calm%20atmosphere&width=1600&height=700&seq=knowledge-hero-2026&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Subtle Geometric Pattern */}
            <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_20%_30%,_#D4A574_1px,_transparent_1px),radial-gradient(circle_at_80%_70%,_#D4A574_1px,_transparent_1px)] bg-[length:60px_60px,80px_80px]" />
            {/* Decorative Glow */}
            <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#D4A574]/8 blur-[120px]" />
            <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#2E8B57]/12 blur-[100px]" />
          </div>

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 md:gap-8">
              {/* Left: Title & Description */}
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-px h-5 bg-[#D4A574]" />
                  <span className="text-xs font-semibold tracking-[0.2em] text-[#D4A574] uppercase">
                    Hearing Knowledge
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] md:leading-[1.2]">
                  聽力健康
                  <span className="block text-[#D4A574] mt-2 md:mt-3">知識庫</span>
                </h1>
                <p className="mt-3 md:mt-4 text-white/88 text-base md:text-lg leading-relaxed max-w-xl">
                  由富聆聽專業聽力師團隊撰寫的聽力保健文章，涵蓋聽力檢測、助聽器保養、噪音防護等主題，
                  幫助您與家人守護聽力健康。
                </p>
                {/* Article Stats */}
                <div className="mt-4 md:mt-5 flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-article-line text-[#D4A574] text-xs" />
                    </div>
                    <span className="whitespace-nowrap">精選文章</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-star-line text-[#D4A574] text-xs" />
                    </div>
                    <span className="whitespace-nowrap">專業聽力師執筆</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-refresh-line text-[#D4A574] text-xs" />
                    </div>
                    <span className="whitespace-nowrap">持續更新</span>
                  </div>
                </div>
              </div>

              {/* Right: Featured metrics */}
              <div className="flex items-center gap-3 md:gap-4 shrink-0">
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">{articles.length || '-'}</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">專業文章</span>
                </div>
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">{Math.max(0, knowledgeCategories.length - 1) || '-'}</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">主題分類</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <svg className="absolute bottom-[-1px] left-0 w-full h-auto block" viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none">
            <path d="M0 100L60 90C120 80 240 60 360 50C480 40 600 38 720 44C840 50 960 62 1080 68C1200 74 1320 74 1380 74L1440 74V100H1380C1320 100 1200 100 1080 100C960 100 840 100 720 100C600 100 480 100 360 100C240 100 120 100 60 100H0Z" fill="white"/>
          </svg>
        </section>

        {/* 篩選與搜尋 */}
        <div className="px-6 md:px-10 lg:px-16 py-8 md:py-10 max-w-7xl mx-auto animate-fade-in-up delay-100">
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            {/* 分類標籤 */}
            <div className="flex flex-wrap gap-2">
              {knowledgeCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 md:px-5 py-2.5 md:py-3 rounded-full text-base md:text-lg font-medium transition-all whitespace-nowrap border ${
                    activeCategory === cat
                      ? 'bg-[#1B3D2A] text-white border-[#1B3D2A]'
                      : 'bg-white text-[#5A6B5A] border-[#E8E0D6] hover:border-[#D4A574] hover:text-[#1B3D2A]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* 搜尋 */}
            <div className="relative flex-1 md:w-72">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-[#9A8A7A] text-base" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜尋文章標題或內容..."
                className="w-full pl-10 pr-4 py-2.5 md:py-3 rounded-xl border border-[#E8E0D6] text-base md:text-lg outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]"
              />
            </div>
          </div>
        </div>

        {/* 文章列表 */}
        <div className="px-6 md:px-10 lg:px-16 pb-12 md:pb-20 max-w-7xl mx-auto animate-fade-in-up delay-200">
          {loadingArticles ? (
            <div>
              <div className="mb-5 flex items-center gap-3 text-sm font-semibold text-[#5A6B5A]">
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#D4A574]" />
                文章資料載入中...
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div className="overflow-hidden rounded-2xl border border-[#E8E0D6] bg-white" key={index}>
                    <div className="h-48 md:h-52 animate-pulse bg-[#f1ece4]" />
                    <div className="space-y-3 p-5 md:p-6">
                      <div className="h-5 w-24 animate-pulse rounded bg-[#f1ece4]" />
                      <div className="h-6 w-4/5 animate-pulse rounded bg-[#f1ece4]" />
                      <div className="h-4 w-full animate-pulse rounded bg-[#f1ece4]" />
                      <div className="h-4 w-2/3 animate-pulse rounded bg-[#f1ece4]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-[#FAF8F5] flex items-center justify-center mx-auto">
                <i className="ri-search-line text-2xl text-[#9A8A7A]" />
              </div>
              <p className="mt-4 text-[#9A8A7A] text-base">沒有符合條件的文章</p>
              <button
                onClick={() => {
                  setActiveCategory(allKnowledgeCategoryLabel);
                  setSearchQuery('');
                }}
                className="mt-4 text-[#1B3D2A] text-base font-medium hover:underline"
              >
                清除篩選
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filtered.map((article) => (
                <div
                  key={article.id}
                  className="bg-white rounded-2xl border border-[#E8E0D6] overflow-hidden cursor-pointer hover:border-[#D4A574]/50 hover:shadow-sm transition-all duration-300 group"
                  onClick={() => navigate(`/article/${article.slug || article.id}`)}
                >
                  <div className="w-full h-48 md:h-52 overflow-hidden relative">
                    <img
                      src={withImageFallback(article.image)}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="inline-block text-xs font-semibold text-[#1B3D2A] bg-[#E8E0D6]/50 px-3 py-1.5 rounded-full">
                        {article.category}
                      </span>
                      <span className="text-base text-[#9A8A7A]">{article.date}</span>
                    </div>
                    <h3 className="text-lg font-bold text-[#1B3D2A] leading-snug group-hover:text-[#D4A574] transition-colors">
                      {article.title}
                    </h3>
                    <p className="mt-3 text-lg text-[#5A6B5A] leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="mt-5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                          <i className="ri-user-line text-[#1B3D2A] text-sm" />
                        </div>
                        <span className="text-sm text-[#9A8A7A]">{article.author}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-[#9A8A7A]">
                        <i className="ri-time-line" />
                        {article.readTime} 分鐘
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 精選文章橫幅 */}
          {featuredArticle && (
          <div className="mt-10 md:mt-16 bg-[#1B3D2A] rounded-2xl overflow-hidden flex flex-col md:flex-row animate-fade-in-up delay-300">
            <div className="flex-1 p-5 md:p-8 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 text-sm text-[#D4A574] font-medium border border-[#D4A574]/40 px-3 py-1.5 rounded-full w-fit">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4A574] inline-block" />
                編輯精選
              </span>
              <h3 className="mt-4 text-lg md:text-xl font-bold text-white leading-snug">
                {featuredArticle.title}
              </h3>
              <p className="mt-3 text-white/70 text-base leading-relaxed">
                {featuredArticle.summary}
              </p>
              <button
                onClick={() => navigate(`/article/${featuredArticle.slug || featuredArticle.id}`)}
                className="mt-5 text-[#D4A574] text-base md:text-lg font-medium flex items-center gap-1 hover:text-white transition-colors w-fit"
              >
                閱讀全文
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </button>
            </div>
            <div className="w-full md:w-72 h-48 md:h-auto overflow-hidden flex-shrink-0">
              <img
                src={withImageFallback(featuredArticle.image)}
                alt={featuredArticle.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          )}

          {/* 聽損與慢性病研究入口 */}
          <div className="mt-6 md:mt-8 rounded-2xl overflow-hidden border border-[#E8E0D6] flex flex-col md:flex-row animate-fade-in-up delay-300 group cursor-pointer hover:border-[#D4A574]/50 transition-all duration-300"
            onClick={() => navigate('/hearing-chronic-disease')}
          >
            <div className="w-full md:w-56 h-44 md:h-auto overflow-hidden flex-shrink-0 relative">
              <img
                src="https://readdy.ai/api/search-image?query=medical%20research%20concept%20hearing%20loss%20and%20chronic%20disease%20connection%20abstract%20modern%20illustration%20with%20stethoscope%20and%20ear%20symbols%20warm%20green%20tones%20clean%20background%20professional&width=500&height=350&seq=89&orientation=landscape"
                alt="聽損與慢性病研究"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex-1 p-5 md:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-2 text-sm text-[#D4A574] font-medium border border-[#D4A574]/40 px-3 py-1.5 rounded-full w-fit">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4A574] inline-block" />
                  深度研究
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-[#1B3D2A] leading-snug group-hover:text-[#D4A574] transition-colors">
                聽損與慢性病：最新醫學研究
              </h3>
              <p className="mt-3 text-[#5A6B5A] text-base leading-relaxed">
                糖尿病、高血壓、心血管疾病與聽力損失有何關聯？國際頂尖期刊研究證實，聽力損失與多種慢性疾病密切相關。了解風險數據、預防策略與整合管理方案。
              </p>
              <div className="mt-4 flex items-center gap-1 text-sm text-[#1B3D2A] font-medium group-hover:text-[#D4A574] transition-colors">
                深入了解
                <i className="ri-arrow-right-line text-sm transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
